import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppSession } from './hooks/useAppSession';
import { normalizePlatformRoles } from '../../server/auth/platformRoles';
import { ADMIN_SESSION_EXPIRED_EVENT } from './adminAuthEvents';

type SessionState = ReturnType<typeof useAppSession>;

function getSessionNotice(sessionState: SessionState, isSessionInvalidated: boolean): string | null {
  if (sessionState.data) {
    return null;
  }

  if (isSessionInvalidated) {
    return 'Session expired. Please sign in again.';
  }

  if (!sessionState.error) {
    return null;
  }

  const message =
    typeof sessionState.error === 'object' && sessionState.error !== null && 'message' in sessionState.error
      ? String(sessionState.error.message ?? '')
      : '';

  if (/session expired|unauthorized/i.test(message)) {
    return 'Session expired. Please sign in again.';
  }

  return 'We could not restore your session. Please sign in again.';
}

function getCurrentRole(sessionState: SessionState): string | null {
  const role = (sessionState.data as { user?: { role?: unknown } } | null)?.user?.role;
  return normalizePlatformRoles(role)[0] ?? null;
}

export function useAdminCapability(options?: { enabled?: boolean; revalidateKey?: string | null }) {
  const enabled = options?.enabled ?? true;
  const sessionState = useAppSession();
  const [sessionRevalidationState, setSessionRevalidationState] = useState<'idle' | 'invalidated' | 'refetching'>(
    'idle',
  );
  const previousRevalidateKeyRef = useRef(options?.revalidateKey ?? null);
  const currentRole = getCurrentRole(sessionState);
  const isSessionInvalidated = sessionRevalidationState !== 'idle';

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const refetch = sessionState.refetch;
    if (typeof refetch !== 'function') {
      return;
    }

    const nextRevalidateKey = options?.revalidateKey ?? null;
    if (previousRevalidateKeyRef.current === nextRevalidateKey) {
      return;
    }

    previousRevalidateKeyRef.current = nextRevalidateKey;
    setSessionRevalidationState('invalidated');
    void refetch();
  }, [enabled, options?.revalidateKey, sessionState.refetch]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const refetch = sessionState.refetch;
    const triggerRefetch = () => {
      setSessionRevalidationState('invalidated');
      if (typeof refetch === 'function') {
        void refetch();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        triggerRefetch();
      }
    };

    window.addEventListener('focus', triggerRefetch);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener(ADMIN_SESSION_EXPIRED_EVENT, triggerRefetch as EventListener);

    return () => {
      window.removeEventListener('focus', triggerRefetch);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener(ADMIN_SESSION_EXPIRED_EVENT, triggerRefetch as EventListener);
    };
  }, [enabled, sessionState.refetch]);

  useEffect(() => {
    if (sessionRevalidationState === 'idle') {
      return;
    }

    if (sessionRevalidationState === 'invalidated' && (sessionState.isPending || sessionState.isRefetching)) {
      setSessionRevalidationState('refetching');
      return;
    }

    if (sessionRevalidationState === 'refetching' && !sessionState.isPending && !sessionState.isRefetching) {
      setSessionRevalidationState('idle');
    }
  }, [sessionRevalidationState, sessionState.data, sessionState.error, sessionState.isPending, sessionState.isRefetching]);

  const hasSession = Boolean(sessionState.data);
  const isCheckingAuth =
    enabled &&
    (sessionState.isPending ||
      (!hasSession && (sessionState.isRefetching || isSessionInvalidated)));
  const isAuthenticated = enabled && hasSession;
  const isAdmin = isAuthenticated && (currentRole === 'admin' || currentRole === 'superadmin');
  const isSuperadmin = isAuthenticated && currentRole === 'superadmin';

  return useMemo(
    () => ({
      session: sessionState.data,
      currentRole,
      isAuthenticated,
      isAdmin,
      isSuperadmin,
      canAccessAdminUi: isAuthenticated,
      canMutate: isAdmin && !isCheckingAuth,
      isCheckingAuth,
      sessionNotice: getSessionNotice(sessionState, isSessionInvalidated),
      refetchSession:
        typeof sessionState.refetch === 'function'
          ? () => {
              setSessionRevalidationState('invalidated');
              return sessionState.refetch();
            }
          : async () => undefined,
    }),
    [currentRole, isAdmin, isAuthenticated, isCheckingAuth, isSessionInvalidated, isSuperadmin, sessionState],
  );
}
